import { Scale } from 'tonal';
import {
  MAX_FRET,
  STANDARD_TUNING,
  chromaOf,
  midiAt,
  openStringMidi,
  stringCount,
  type FretRange,
  type Position,
} from './fretboard';
import { relativeMajor, relativeMinor, type ScaleDegree } from './harmony';
import { chromaticDegree, majorScale, type ChromaticDegree } from './scales';
import { cagedShape, shapeSpan, type CagedShape, type CagedShapeName } from './shapes';

/**
 * Pentatonic boxes and major-scale positions (topics C7, I7), standard tuning.
 *
 * Box numbering follows the usual convention: box 1 starts on the root on
 * string 6, and each box starts on the next scale note. That makes box N wrap
 * the same CAGED shape in both qualities: 1 → E, 2 → D, 3 → C, 4 → A, 5 → G
 * (minor shapes for minor boxes, major shapes for major boxes). Boxes chain up
 * the neck in that order, sharing their edge notes, which is what breaks "box
 * prison" (research/03 C7).
 *
 * The relative link: a minor box is the major box one number lower of its
 * relative major (A minor box 1 = C major box 5, both around the G shape of C).
 *
 * Major-scale positions (I7) build on the same five boxes: the CAGED position
 * is the major box with its two gaps, the 4 and the 7, filled in. The
 * 3-notes-per-string system is `majorScale3nps`.
 */

export type PentatonicQuality = 'major' | 'minor';
export type BoxNumber = 1 | 2 | 3 | 4 | 5;

                                                                                     
export const BOX_NUMBERS: readonly BoxNumber[] = [1, 2, 3, 4, 5];

/** The CAGED shape each box wraps, index = box − 1. */
                                                                        

export interface ScaleNote extends Position {
  /** Pitch class as the scale spells it. */
  readonly pc: string;
}

export interface PentatonicNote extends ScaleNote {
  /** Degree relative to the box's own root: 1 b3 4 5 b7 (minor) or 1 2 3 5 6 (major). */
  readonly degree: ChromaticDegree;
}

export interface PentatonicBox {
  readonly root: string;
  readonly quality: PentatonicQuality;
  readonly box: BoxNumber;
  /** The CAGED shape the box wraps (of the same root and quality). */
  readonly shape: CagedShapeName;
  readonly span: FretRange;
  /** Ascending in pitch: string 6 first, two notes per string. */
  readonly notes: readonly PentatonicNote[];
}

export interface PlacementOptions {
  /** Place at the lowest position whose frets are all ≥ this. Default 0. */
  readonly minFret?: number;
}

                                                           
                                                                                                 
 

/** The five notes of the scale, root first, spelled by tonal: A C D E G; C D E G A. */
                                                                                     
                                                                 
                                                                              
               
 

                                                          
                 
                                               
 

                                                               
                                                      
 

/** The box above (5 wraps round to 1, an octave up). */
                                                    
                 
                                      
 

/** The box below (1 wraps round to 5). */
                                                        
                 
                                            
 

/**
 * A scale run up the neck from string 6: `perString` consecutive scale notes
 * on each string, starting on `scale[startIndex]`. Placed at the lowest
 * position whose frets are all ≥ `minFret`. Pentatonic boxes are 2 per string,
 * 3NPS scale patterns 3 per string.
 */
                                  
                           
                     
                    
                              
                
                                    
                                                 
                                  
                                                                                        
                                            
                                                                        
                                                                 
                                                   
                                                                
                                                                      
                                                                          
   
                                                                  
                
                                                           
                                              
                                                                                 
   
                                                                                                 
 

                                                        
                              
 

/** Box `box` of the root's pentatonic, e.g. A minor box 1 at frets 5–8. */
                              
               
                             
                 
                              
                  
                 
                                               
                                                                        
         
                                        
      
          
                             
            
        
                         
                        
          
    
 

/** All five boxes, chained up the neck from the lowest position. */
                                                                                            
                                                                      
                                        
    
 

/** The relative scale: A minor ↔ C major. Same five notes, different root. */
                                   
               
                             
                                                 
                            
                                                     
                                                      
 

/** The number the same fingering has in the relative scale: minor N = major N − 1. */
                                                                                          
                                                               
 

                                                                                    
                                                                                           
 

/**
 * The same box read in the relative scale, at the same frets: A minor box 1
 * (5–8) is C major box 5.
 */
                                                                
                                                        
                                                         
                                                                                  
 

/**
 * The CAGED chord the box wraps, placed inside it: A minor box 1 at fret 5
 * holds the E-shape Am barre at fret 5.
 */
                                                               
                                                                                           
 

/**
 * The box around a major or minor CAGED shape (the inverse of
 * `boxChordShape`). A box reaches at most two frets below its shape.
 */
                                                                  
                                      
                                                                                  
   
                                     
                                                                                  
                                   
     
                                          
                                                                                                   
   
             
 

/**
 * Positions two boxes share. Neighbouring boxes overlap on one note per
 * string, which is how one box leads into the next.
 */
                            
                                             
                                             
               
                
                                                                                   
                                                      
 

// ---------------------------------------------------------------------------
// Major scale in five positions (I7)

export interface MajorScaleNote extends ScaleNote {
  readonly degree: ScaleDegree;
}

export interface MajorScalePosition {
  readonly tonic: string;
  /** Same numbering as the major pentatonic boxes: 1 = E shape … 5 = G shape. */
  readonly position: BoxNumber;
  readonly shape: CagedShapeName;
  readonly span: FretRange;
  /** Ascending in pitch. */
  readonly notes: readonly MajorScaleNote[];
}

                                                                                      
                             
                                                      
                         
                                                                            
 

/**
 * The major scale in one CAGED position: the matching major pentatonic box
 * with its gaps (the 4 and 7) filled in, plus the scale note just below and
 * just above the box when they fall inside its frets. A gap between two
 * strings goes on whichever string keeps it nearer the box's frets (the lower
 * fret on a tie). C major position 1 (E shape) is frets 7–10.
 */
                                   
                
                      
                              
                       
                                                            
                                  
                                                                                 
                                
                                                                                            
                                                                  
                                                           
           
                                        
     

                               
                                               
                                                                     
                                      
                                                     
                               
                  
                               
                   
                                                              
                                   
                                                                            
                                                                                   
                                         
     
     
                                                                 
                                                                   
                                      
                                                      

                                                                          
          
                              
             
                     
                        
          
    
 

/** All five positions, chained up the neck from the lowest. */
                                                                          
                                                                   
                                        
    
 

/**
 * "Start from any degree" (I7): the notes of a position in ascending order,
 * from the lowest note on `degree` to the highest note on it.
 */
                            
                                                 
                      
                     
                                                                     
                                                                       
                                                                                  
                                               
 

/**
 * The major scale three notes per string, starting on `startDegree` on string
 * 6 (7 patterns, one per degree).
 */
                               
                
                           
                              
                     
                                  
                                                                         
         
                                                     
      
 
